/**
 * Content / feed domain constants and pure helpers.
 *
 * The app serves two "channels":
 *   - CHILL  -> contentId "soda"
 *   - NSFW   -> contentId stored under `nsfwContentId` (default "xx"),
 *               can be overridden by the `?ct=` query param.
 */
import { getItem, setItem } from './storage.js';
import { normalisePath, stripTrailingSlash } from './format.js';

export const API_BASE = stripTrailingSlash('https://tgcdn.xsoda.net');
export const POSTER_BASE = stripTrailingSlash('https://static.xsoda.net');

export const CHILL_CONTENT_ID = 'soda';
export const DEFAULT_NSFW_CONTENT_ID = 'xx';

/** Fixed referral link used by the Share action. */
export const SHARE_URL =
  'https://play.google.com/store/apps/details?id=com.puer.browser&referrer=refshare';

const NSFW_CONTENT_KEY = 'nsfwContentId';
const USER_CONTENT_KEY = 'userContentId';
const LAST_VIEWED_SUFFIX = 'lastViewed';

/** The four reactions, in display order: [key, emoji]. */
export const REACTIONS = Object.freeze([
  ['like', '👍'],
  ['heart', '❤️'],
  ['shock', '😱'],
  ['fire', '🔥'],
]);

export const REACTION_EMOJI = Object.freeze({
  like: '👍',
  heart: '❤️',
  shock: '😱',
  fire: '🔥',
});

/** The NSFW channel id currently configured for this user. */
export function getNsfwContentId() {
  return getItem(NSFW_CONTENT_KEY) || DEFAULT_NSFW_CONTENT_ID;
}

/**
 * Resolve the active content id from URL params / storage (idempotent).
 *  - `?ct=<id>`     -> set NSFW channel + persist as current.
 *  - `?refresh`     -> reset to CHILL ("soda").
 *  - otherwise      -> last stored channel, defaulting to CHILL.
 */
export function resolveContentId() {
  const params = new URLSearchParams(window.location.search);
  const ct = params.get('ct');
  if (ct) {
    setItem(NSFW_CONTENT_KEY, ct);
    setItem(USER_CONTENT_KEY, ct);
    return ct;
  }
  if (params.get('refresh')) {
    setItem(USER_CONTENT_KEY, CHILL_CONTENT_ID);
    return CHILL_CONTENT_ID;
  }
  const stored = getItem(USER_CONTENT_KEY) || CHILL_CONTENT_ID;
  setItem(USER_CONTENT_KEY, stored);
  return stored;
}

/** Persist the chosen channel and clean transient query params from the URL. */
export function selectContentId(contentId) {
  setItem(USER_CONTENT_KEY, contentId);
  const params = new URLSearchParams(window.location.search);
  params.delete('ct');
  params.delete('refresh');
  const qs = params.toString();
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${qs ? `?${qs}` : ''}`,
  );
}

export const isChillChannel = (contentId) => contentId === CHILL_CONTENT_ID;

/** Coarse channel kind used to scope per-mode data (favorites, etc.). */
export const channelKind = (contentId) =>
  isChillChannel(contentId) ? 'chill' : 'nsfw';

/* ------------------------------------------------------------------ *
 * Resume position ("last viewed" video per channel)
 * ------------------------------------------------------------------ */

export function getLastViewed(contentId) {
  return getItem(`${contentId}${LAST_VIEWED_SUFFIX}`);
}

export function setLastViewed(contentId, videoId) {
  setItem(`${contentId}${LAST_VIEWED_SUFFIX}`, String(videoId));
}

/* ------------------------------------------------------------------ *
 * Network-aware preload window
 * ------------------------------------------------------------------ */

/** Number of neighbouring slides to preload (1 on slow links, else 2). */
export function preloadRadius() {
  const c = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (
    c &&
    (c.saveData ||
      c.effectiveType === 'slow-2g' ||
      c.effectiveType === '2g' ||
      c.effectiveType === '3g')
  ) {
    return 1;
  }
  return 2;
}

/* ------------------------------------------------------------------ *
 * URL builders
 * ------------------------------------------------------------------ */

/**
 * Build the per-video media/URL helpers from a resolved feed config.
 * Each builder accepts a full video item and honours explicit per-item
 * overrides (`playlist` / `poster` / `avatar`), which lets the mock/demo
 * feed point at arbitrary streams without a CDN.
 */
export function makeUrlBuilders(feed) {
  return {
    playlistUrl: (item) =>
      item.playlist || `${feed.cdnUrl}${feed.cdnPath}${item.id}`,
    avatarUrl: (item) =>
      item.avatar || (feed.imageBase ? `${feed.imageBase}/${item.id}_ava.webp` : ''),
    posterUrl: (item) =>
      item.poster || (POSTER_BASE && item.id ? `${POSTER_BASE}/${item.id}.webp` : ''),
  };
}

/** Random but plausible starting reaction counts for a fresh video. */
export function seedReactions() {
  return {
    like: Math.floor(Math.random() * 5e6),
    heart: Math.floor(Math.random() * 3e6),
    shock: Math.floor(Math.random() * 5e5),
    fire: Math.floor(Math.random() * 1e5),
  };
}

/** Normalise a raw API response into the feed config we use internally. */
export function parseFeedConfig(json) {
  return {
    cdnUrl: stripTrailingSlash(json.cdn_url || API_BASE),
    cdnPath: normalisePath(json.cdn_path || '/playlist/'),
    imageBase: json.image_base
      ? `${stripTrailingSlash(
          json.image_base_host || json.cdn_url || API_BASE,
        )}${normalisePath(json.image_base)}`
      : '',
  };
}
